import { useEffect, useRef, useState } from 'react';
import ApiClientManager from 'core/apiClient';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { useSwitchTenantMutation } from 'TenantModule/api/queries/useSwitchTenant';

interface UseEnsureTeamContextResult {
  /** True when the tenant context is ready for data fetching */
  isContextReady: boolean;
  /** True while a tenant switch is in progress */
  isSwitchingContext: boolean;
}

/**
 * Ensures the Rails session tenant matches the URL tenant param before data fetching.
 *
 * When users access the Team Details page via direct URL (not from the team lookup table),
 * the Rails backend session may still be set to their default tenant. This hook detects
 * the mismatch and calls switchTenant() to update the session, matching the pattern used
 * by TeamsV2Page.handleRowClick and useUrlTeamNavigation.
 *
 * Only tenant switching is needed — realm routing is handled entirely via request headers
 * (handleSelectGlobalRailsSandbox is a no-op).
 *
 * The Galaxy lookup API (useSearchTeamsByFieldsQuery) works fine without this because
 * it routes entirely via headers. This hook is needed for Rails API calls (fetchTeams,
 * fetchTeamMembers, fetchIntegrations) that rely on the session.
 *
 * @see src/hooks/useUrlTeamNavigation.tsx - Same switchTenant pattern for deep links
 * @see src/modules/TeamModule/components/TeamsV2Page.tsx - Same pattern in handleRowClick
 */
const useEnsureTeamContext = ({
  tenant,
}: {
  tenant: string | undefined;
}): UseEnsureTeamContextResult => {
  const [isContextReady, setIsContextReady] = useState(false);
  const hasAttemptedSwitch = useRef(false);
  const lastProcessedTenantRef = useRef<string | undefined>(undefined);

  const { tenantOrder, tenantsHash } = useTenants({ isOff: false });
  const { switchTenant, isSwitchingTenant } = useSwitchTenantMutation();

  useEffect(() => {
    // Detect tenant changes while the component stays mounted (e.g., navigating
    // between teams on different tenants via React Router param changes).
    // Reset switch state so we re-validate/switch for the new tenant.
    if (tenant !== lastProcessedTenantRef.current) {
      lastProcessedTenantRef.current = tenant;
      hasAttemptedSwitch.current = false;
      setIsContextReady(!tenant);
    }

    // No tenant specified in URL - use whatever context the user logged in with
    if (!tenant) {
      setIsContextReady(true);
      return;
    }

    // Don't re-attempt if we already tried for this tenant
    if (hasAttemptedSwitch.current) {
      return;
    }

    // Wait for tenant data to load before checking
    if (
      !tenantOrder ||
      tenantOrder.length === 0 ||
      !tenantsHash ||
      Object.keys(tenantsHash).length === 0
    ) {
      return;
    }

    // Check if already on the correct tenant
    const currentTenantName = ApiClientManager.getAuthDefaultTenant()?.tenantName;

    if (currentTenantName === tenant) {
      setIsContextReady(true);
      hasAttemptedSwitch.current = true;
      return;
    }

    // Validate the tenant exists in our known tenants
    const tenantId = tenantOrder.find((tid) => tenantsHash[tid]?.tenantName === tenant);

    if (!tenantId) {
      // Tenant not found - proceed anyway (headers will handle routing)
      setIsContextReady(true);
      hasAttemptedSwitch.current = true;
      return;
    }

    // Switch the Rails session to the correct tenant
    hasAttemptedSwitch.current = true;
    switchTenant(
      { tenant },
      {
        onSuccess: () => {
          setIsContextReady(true);
        },
        onError: () => {
          // Still mark as ready to avoid infinite loading.
          // useSwitchTenantMutation already shows an error toast.
          setIsContextReady(true);
        },
      },
    );
  }, [tenant, tenantOrder, tenantsHash, switchTenant]);

  return {
    isContextReady,
    isSwitchingContext: isSwitchingTenant,
  };
};

export default useEnsureTeamContext;
